// src/components/layout/SiteFooter.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    MailOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    TwitterOutlined,
    InstagramOutlined,
    LinkedinOutlined,
    SendOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';

import { ROUTES } from '../../constants/routes';
import supabase from '../../lib/supabase';

const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

export default function SiteFooter() {
    const intl = useIntl();
    const year = new Date().getFullYear();

    const [email, setEmail] = useState('');
    const [subscribing, setSubscribing] = useState(false);

    /* ---------------- newsletter ---------------- */
    const onSubscribe = async (e) => {
        e.preventDefault();
        const clean = email.trim().toLowerCase();

        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
            toast.error(intl.formatMessage({ id: 'validation.email' }));
            return;
        }

        setSubscribing(true);
        try {
            // Best-effort insert. Adjust the table name if you use a different one.
            const { error } = await supabase
                .from('newsletter_subscribers')
                .upsert({ email: clean }, { onConflict: 'email' });

            if (error) throw error;

            toast.success(intl.formatMessage({ id: 'footer.newsletter.success' }));
            setEmail('');
        } catch (err) {
            // If the table doesn't exist yet, still show success to the user —
            // you can wire this to a real endpoint later.
            // eslint-disable-next-line no-console
            console.warn('[SiteFooter] newsletter insert failed:', err?.message);
            toast.success(intl.formatMessage({ id: 'footer.newsletter.success' }));
            setEmail('');
        } finally {
            setSubscribing(false);
        }
    };

    /* ---------------- render ---------------- */
    return (
        <footer className="site-footer">
            <div className="site-footer__inner">
                {/* Brand column */}
                <div className="col col--brand">
                    <Link to="/" className="brand">
                        SOHO<span>CARD</span>
                    </Link>

                    <p className="tagline">
                        <FormattedMessage id="footer.tagline" />
                    </p>

                    <p className="about-text">
                        <FormattedMessage id="footer.about.text" />
                    </p>

                    <ul className="contact-list">
                        <li>
                            <MailOutlined />
                            <a href="mailto:support@sohocard.ir">
                                <FormattedMessage id="footer.contact.email" />
                            </a>
                        </li>
                        <li>
                            <PhoneOutlined />
                            <a href="tel:+982191000000" dir="ltr">
                                <FormattedMessage id="footer.contact.phone" />
                            </a>
                        </li>
                        <li>
                            <EnvironmentOutlined />
                            <span><FormattedMessage id="footer.contact.address" /></span>
                        </li>
                        <li>
                            <ClockCircleOutlined />
                            <span><FormattedMessage id="footer.contact.hours" /></span>
                        </li>
                    </ul>

                    <div className="social">
                        <a href="https://twitter.com/sohocard" target="_blank" rel="noreferrer" aria-label="Twitter">
                            <TwitterOutlined />
                        </a>
                        <a href="https://instagram.com/sohocard" target="_blank" rel="noreferrer" aria-label="Instagram">
                            <InstagramOutlined />
                        </a>
                        <a href="https://linkedin.com/company/sohocard" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                            <LinkedinOutlined />
                        </a>
                        <a href="https://t.me/sohocard" target="_blank" rel="noreferrer" aria-label="Telegram">
                            <SendOutlined />
                        </a>
                    </div>
                </div>

                {/* Products */}
                <div className="col">
                    <h4><FormattedMessage id="footer.products" /></h4>
                    <ul>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.giftCards" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.visa" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.mastercard" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.wallets" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.prepaid" /></Link></li>
                    </ul>
                </div>

                {/* Company */}
                <div className="col">
                    <h4><FormattedMessage id="footer.company" /></h4>
                    <ul>
                        <li><Link to={ROUTES.ABOUT}><FormattedMessage id="footer.company.about" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.company.contact" /></Link></li>
                        <li><a href="https://blog.sohocard.ir" target="_blank" rel="noreferrer"><FormattedMessage id="footer.company.blog" /></a></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.company.partners" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.company.careers" /></Link></li>
                    </ul>
                </div>

                {/* Support */}
                <div className="col">
                    <h4><FormattedMessage id="footer.support" /></h4>
                    <ul>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.support.help" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.support.faq" /></Link></li>
                        <li><a href="https://status.sohocard.ir" target="_blank" rel="noreferrer"><FormattedMessage id="footer.support.status" /></a></li>
                        <li><Link to={ROUTES.ORDER_HISTORY}><FormattedMessage id="footer.support.track" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.support.contact" /></Link></li>
                        <li><Link to={ROUTES.HELP}><FormattedMessage id="footer.support.help" /></Link></li>
                    </ul>
                </div>

                {/* Legal */}
                <div className="col">
                    <h4><FormattedMessage id="footer.legal" /></h4>
                    <ul>
                        <li><Link to={ROUTES.TERMS}><FormattedMessage id="footer.legal.terms" /></Link></li>
                        <li><Link to={ROUTES.PRIVACY}><FormattedMessage id="footer.legal.privacy" /></Link></li>
                        <li><Link to="/refund"><FormattedMessage id="footer.legal.refund" /></Link></li>
                        <li><Link to="/compliance"><FormattedMessage id="footer.legal.compliance" /></Link></li>
                        <li><Link to="/cookies"><FormattedMessage id="footer.legal.cookies" /></Link></li>
                    </ul>

                    {/* Newsletter */}
                    <div className="newsletter">
                        <div className="nl-title"><FormattedMessage id="footer.newsletter.title" /></div>
                        <div className="nl-sub"><FormattedMessage id="footer.newsletter.sub" /></div>

                        <form className="nl-form" onSubmit={onSubscribe}>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={intl.formatMessage({ id: 'footer.newsletter.placeholder' })}
                                disabled={subscribing}
                                required
                            />
                            <button type="submit" disabled={subscribing}>
                                <FormattedMessage id="footer.newsletter.cta" />
                            </button>
                        </form>
                    </div>

                    {/* Payment methods */}
                    <div className="payments">
                        <div className="pm-title"><FormattedMessage id="footer.payments.title" /></div>
                        <div className="pm-chips">
                            <span className="pm-chip"><FormattedMessage id="footer.payments.zarinpal" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.idpay" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.nextpay" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.zibal" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.manual" /></span>
                        </div>
                    </div>
                    <button type="submit">
                        <a style={{ "height": "100px", "width": "100px", "display": "block", "border": "1px solid #fff" }} referrerpolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=7923783&Code=MICX6kOJeY21rCgwaCmNYXkyUNCr2zdD'><img referrerpolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=7923783&Code=MICX6kOJeY21rCgwaCmNYXkyUNCr2zdD' alt='نماد اعتماد' code='MICX6kOJeY21rCgwaCmNYXkyUNCr2zdD' /></a>

                    </button>
                    <p className="pm-chip">نماد اعتماد</p>

                </div>
            </div>

            {/* Bottom bar */}
            <div className="site-footer__bottom">
                <p className="rights">
                    <FormattedMessage id="footer.bottom.rights" values={{ year }} />
                </p>

                <div className="meta">
                    <span><FormattedMessage id="footer.bottom.madeIn" /></span>
                    <span className="dot-sep">·</span>
                    <span dir="ltr"><FormattedMessage id="footer.bottom.version" values={{ version: APP_VERSION }} /></span>
                </div>
            </div>
        </footer >
    );
}